<template>
  <div class="worker-campus-single-select">
    <CustomThreeSelect
      :options="options"
      :multiple="false"
      :value="value"
      :placeholder="placeholder"
      :disabled="disabled || !campusId"
      :open-direction="openDirection"
      :hide-label="true"
      @input="onInput"
      @dropdown-open="onOpen"
      @searchChange="onSearchChange"
    >
      <template #option-label="{ node }">
        <label class="worker-campus-single-select__option">
          <img
            v-if="node.raw && node.raw.image_path"
            :src="node.raw.image_path"
            alt=""
            class="worker-campus-single-select__avatar"
            v-avatar-placeholder
          />
          <span class="worker-campus-single-select__option-text">{{
            node.label
          }}</span>
        </label>
      </template>
      <template #value-label="{ node }">
        <span v-if="node" class="worker-campus-single-select__value">
          <img
            v-if="node.raw && node.raw.image_path"
            :src="node.raw.image_path"
            alt=""
            class="
              worker-campus-single-select__avatar
              worker-campus-single-select__avatar--sm
            "
            v-avatar-placeholder
          />
          <span>{{ node.label }}</span>
        </span>
      </template>
      <template #after-list>
        <div v-if="loading" class="worker-campus-single-select__pager">
          טוען...
        </div>
        <div v-else-if="hasMore" class="worker-campus-single-select__pager">
          <button
            type="button"
            class="worker-campus-single-select__more"
            :disabled="loadingMore"
            @click.stop="loadMore"
          >
            {{ loadingMore ? 'טוען...' : 'טען עוד' }}
          </button>
        </div>
      </template>
    </CustomThreeSelect>
  </div>
</template>

<script>
import axios from 'axios';
import CustomThreeSelect from './CustomThreeSelect.vue';

export default {
  name: 'WorkerCampusSingleSelect',
  components: {
    CustomThreeSelect
  },
  props: {
    value: {
      type: [Number, String],
      default: null
    },
    campusId: {
      type: [Number, String],
      default: null
    },
    placeholder: {
      type: String,
      default: 'בחר עובד'
    },
    openDirection: {
      type: String,
      default: 'bottom'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    perPage: {
      type: Number,
      default: 30
    }
  },
  data() {
    return {
      options: [],
      loading: false,
      loadingMore: false,
      searchTerm: '',
      nextPage: 1,
      hasMore: false
    };
  },
  methods: {
    onInput(v) {
      this.$emit('input', v);
      if (v == null) {
        return;
      }
      const opt = this.options.find(o => o.id === v);
      if (opt) {
        this.$emit('pick', {
          userId: opt.id,
          label: opt.label,
          image_path: opt.image_path || null
        });
      }
    },
    onOpen() {
      if (!this.campusId) {
        return;
      }
      if (this.options.length === 0 && !this.loading) {
        this.reloadFromStart();
      }
    },
    onSearchChange(search) {
      const next = search || '';
      if (this.searchTerm === next) {
        return;
      }
      this.searchTerm = next;
      this.reloadFromStart();
    },
    reloadFromStart() {
      this.nextPage = 1;
      this.options = [];
      this.hasMore = false;
      this.loadPage(false);
    },
    mapWorkerRows(rows) {
      const list = Array.isArray(rows) ? rows : [];
      return list.map(w => {
        const name = [w.name, w.last_name].filter(Boolean).join(' ').trim();
        return {
          id: w.user_id,
          label: name || '—',
          image_path: w.image_path || null,
          img: w.img || null
        };
      });
    },
    async loadPage(append) {
      if (!this.campusId) {
        return;
      }
      if (this.loading || this.loadingMore) {
        return;
      }
      if (append) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }
      try {
        const { data } = await axios.get(
          '/api/main-info/multi-users-select/worker-list',
          {
            params: {
              campus_id: this.campusId,
              page: this.nextPage,
              search: this.searchTerm || undefined,
              per_page: this.perPage
            }
          }
        );
        const rows = Array.isArray(data.data) ? data.data : [];
        const chunk = this.mapWorkerRows(rows);
        if (append) {
          this.options = this.options.concat(chunk);
        } else {
          this.options = chunk;
        }
        const meta = data.meta || {};
        const lastPage = meta.last_page != null ? meta.last_page : 1;
        const current = meta.current_page != null ? meta.current_page : 1;
        this.hasMore = current < lastPage;
        if (this.hasMore) {
          this.nextPage = current + 1;
        }
      } catch (e) {
        console.error('WorkerCampusSingleSelect', e);
        this.$toastr &&
          this.$toastr.e &&
          this.$toastr.e('שגיאה בטעינת רשימת העובדים');
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    loadMore() {
      if (!this.hasMore || this.loadingMore || !this.campusId) {
        return;
      }
      this.loadPage(true);
    }
  }
};
</script>

<style lang="scss" scoped>
.worker-campus-single-select {
  ::v-deep .vue-treeselect__menu {
    max-height: 280px !important;
  }
}
.worker-campus-single-select__option {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 0;
  font: inherit;
  cursor: pointer;
}
.worker-campus-single-select__option-text {
  min-width: 0;
}
.worker-campus-single-select__value {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.worker-campus-single-select__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
.worker-campus-single-select__avatar--sm {
  width: 28px;
  height: 28px;
}
.worker-campus-single-select__pager {
  padding: 8px 12px;
  text-align: center;
  border-top: 1px solid #e3e7ee;
  font-size: 12px;
  color: #5a6f95;
}
.worker-campus-single-select__more {
  background: #409fff;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 12px;
  transition: 0.2s;
}
.worker-campus-single-select__more:hover:not(:disabled) {
  background: #0077ef;
}
.worker-campus-single-select__more:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
